/**
Project: Voice Panel (c)
Title: Service Number Actions 
Description: Service numbers actions for getting/modifying service numbers records
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import axios from "axios";
import { includes } from "lodash";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  logMessage,
  checkIdentitySession,
  getSessionExpiredError,
} from "../../../../core/actions/common-actions";
import {
  SERVICE_NUMBERS_ROUTING_POLICIES,
  SERVICE_NUMBERS_ALL_RECORDS,
  SERVICE_NUMBERS_DISPLAY_RECORDS,
  SERVICE_NUMBERS_PAGE_RECORDS,
  SERVICE_NUMBERS_UPDATE_RECORDS,
  SERVICE_NUMBER_PHONE_NUMBER_TO_ALTER,
  SERVICE_NUMBER_ROUTING_POLICY_TO_ALTER,
  ALTER_SERVICE_NUMBER_PHONE_NUMBER,
  ALTER_SERVICE_NUMBER_ROUTING_POLICY,
  SERVICE_NUMBERS_DATA_RESET,
} from "../../../../store/voice-admin/constants";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import IntlUtil from "../../../../core/helpers/intl-util";

const actionFileName = "service-number-actions.js";

// Get Service Numbers
export function getServiceNumbers(policyId, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      let url = "/service-numbers";
      if (policyId !== null && policyId.length > 0) {
        url = url + "?policyId=" + policyId;
      }
      await axios
        .get(AppConfigProps.serverRoutePrefix + url, cancelToken)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getServiceNumbers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getServiceNumbersRoutingPolicies(cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/routing-policies?policyStatus=1",
          cancelToken
        )
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            let policies = [];
            res.data.records.forEach((policy) => {
              if (policy.policyStatus === 1) {
                policies.push(policy.policyId);
              }
            });
            dispatch({
              type: SERVICE_NUMBERS_ROUTING_POLICIES,
              payload: policies,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getServiceNumbersRoutingPolicies()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

// Get Service Numbers Propagation information
export function getServiceNumbersPropagation(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/service-numbers/propagation",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getServiceNumbersPropagation()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

// Update Service Numbers (Apply Changes)
export function updateServiceNumbers(serviceNumberData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .put(
          AppConfigProps.serverRoutePrefix + "/service-numbers",
          serviceNumberData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">updateServiceNumbers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function setServiceNumbersAllRecords(serviceNumbersAllRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: SERVICE_NUMBERS_ALL_RECORDS,
        payload: serviceNumbersAllRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setServiceNumbersDisplayRecords(serviceNumbersDisplayRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: SERVICE_NUMBERS_DISPLAY_RECORDS,
        payload: serviceNumbersDisplayRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setServiceNumbersUpdateRecords(serviceNumbersUpdateRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: SERVICE_NUMBERS_UPDATE_RECORDS,
        payload: serviceNumbersUpdateRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setServiceNumbersPageRecords(serviceNumbersPageRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: SERVICE_NUMBERS_PAGE_RECORDS,
        payload: serviceNumbersPageRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function sortServiceNumbers(list, sortColumn, sortOrder) {
  return new Promise(async function (resolve, reject) {
    if (list && list.length > 0) {
      let sortRecords = list.sort((firstItem, secondItem) => {
        if (sortOrder === VoiceAdminConstants.TABLE_DATA.SORT_ORDER.ASCENDING) {
          return firstItem[sortColumn].localeCompare(
            secondItem[sortColumn],
            "en",
            {
              numeric: true,
            }
          );
        } else {
          return secondItem[sortColumn].localeCompare(
            firstItem[sortColumn],
            "en",
            {
              numeric: true,
            }
          );
        }
      });
      resolve(sortRecords);
    } else {
      resolve(list);
    }
  });
}

export function sliceServiceNumbers(list, pageOffset, pageLimit) {
  return new Promise(async function (resolve, reject) {
    if (list && list.length > 0 && pageOffset >= 0 && pageLimit > 0) {
      let start = pageOffset * pageLimit;
      let end = start + pageLimit;
      if (end > list.length) end = list.length;

      let sliceRecords = await list.slice(start, end);
      resolve(sliceRecords);
    } else {
      resolve(list);
    }
  });
}

export function searchServiceNumbers(list, searchKeyword) {
  return new Promise(async function (resolve, reject) {
    if (
      list &&
      list.length > 0 &&
      searchKeyword &&
      searchKeyword.trim().length > 0
    ) {
      let searchRecords = list.filter((serviceNumber) => {
        return (
          includes(
            serviceNumber.name.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          ) ||
          includes(
            serviceNumber.email.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          ) ||
          includes(serviceNumber.phone, searchKeyword.trim()) ||
          includes(
            serviceNumber.policyId.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          ) ||
          includes(
            serviceNumber.serviceType.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          )
        );
      });

      resolve(searchRecords);
    } else {
      resolve(list);
    }
  });
}

export function setServiceNumberPhoneNumberToAlter(id) {
  return async function (dispatch) {
    try {
      dispatch({
        type: SERVICE_NUMBER_PHONE_NUMBER_TO_ALTER,
        payload: id,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setServiceNumberRoutingPolicyToAlter(id) {
  return async function (dispatch) {
    try {
      dispatch({
        type: SERVICE_NUMBER_ROUTING_POLICY_TO_ALTER,
        payload: id,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function updateServiceNumberPhoneNumber(serviceNumber) {
  return async function (dispatch) {
    try {
      dispatch({
        type: ALTER_SERVICE_NUMBER_PHONE_NUMBER,
        payload: serviceNumber,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function updateServiceNumberRoutingPolicy(serviceNumber) {
  return async function (dispatch) {
    try {
      dispatch({
        type: ALTER_SERVICE_NUMBER_ROUTING_POLICY,
        payload: serviceNumber,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function resetServiceNumbersData() {
  return async function (dispatch) {
    try {
      dispatch({
        type: SERVICE_NUMBERS_DATA_RESET,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

// Get Service Numbers Propagation update result file
export function getServiceNumbersPropagationFileData(
  intlNamespace,
  propagationData
) {
  let headerColumns = [];
  headerColumns.push(
    IntlUtil.getText(intlNamespace, "content.displayName").replace(
      new RegExp(" ", "g"),
      ""
    ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.userName").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.callingPolicy").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.serviceType").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.updateStatus").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.errorMessage").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "\n"
  );

  let serviceNumberData = [];
  if (propagationData && propagationData.length > 0) {
    propagationData.forEach((propItem) => {
      const serviceNumberIndex = serviceNumberData.findIndex(
        (serviceNumberItem) => serviceNumberItem.id === propItem.id
      );
      if (serviceNumberIndex >= 0) {
        // Second Item
        let serviceNumberItem = serviceNumberData[serviceNumberIndex];
        if (
          serviceNumberData[serviceNumberIndex].updateStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_SUCCESS
        ) {
          // If First is Success & Second is Failure
          if (
            propItem.updateStatus ===
            VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
          ) {
            if (propItem.paramName === "Phone") {
              serviceNumberItem.phone = propItem.paramValue;
            }
            if (propItem.paramName === "Policy") {
              serviceNumberItem.policyId = propItem.paramValue;
            }
            serviceNumberItem.updateStatus = IntlUtil.getText(
              intlNamespace,
              "content.failure"
            );
          }
        } else if (
          serviceNumberData[serviceNumberIndex].updateStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
        ) {
          // If First is Failure & Second is Failure
          if (
            propItem.updateStatus ===
            VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
          ) {
            if (propItem.paramName === "Phone") {
              serviceNumberItem.phone = propItem.paramValue;
            }
            if (propItem.paramName === "Policy") {
              serviceNumberItem.policyId = propItem.paramValue;
            }
          }
        }
        if (propItem.errorMessage)
          serviceNumberItem.errorMessage =
            serviceNumberItem.errorMessage &&
            serviceNumberItem.errorMessage.length > 0
              ? serviceNumberItem.errorMessage + " " + propItem.errorMessage
              : propItem.errorMessage;

        serviceNumberData[serviceNumberIndex] = serviceNumberItem;
      } else {
        // First Item
        if (
          propItem.updateStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
        ) {
          if (propItem.paramName === "Phone") {
            propItem.phone = propItem.paramValue;
          }
          if (propItem.paramName === "Policy") {
            propItem.policyId = propItem.paramValue;
          }
        }
        serviceNumberData.push(propItem);
      }
    });
  }
  let dataColumns = [];
  if (serviceNumberData && serviceNumberData.length > 0) {
    dataColumns = serviceNumberData.map((serviceNumber) => {
      let displayName = "";
      if (serviceNumber.name !== null && serviceNumber.name.trim().length > 0)
        displayName = serviceNumber.name.replace(new RegExp(",", "g"), "");
      let userName = "";
      if (serviceNumber.email !== null && serviceNumber.email.trim().length > 0)
        userName = serviceNumber.email.replace(new RegExp(",", "g"), "");
      let phoneNumber = "";
      if (serviceNumber.phone !== null && serviceNumber.phone.trim().length > 0)
        phoneNumber = serviceNumber.phone.replace(new RegExp(",", "g"), "");
      let callingPolicy = "";
      if (
        serviceNumber.policyId !== null &&
        serviceNumber.policyId.trim().length > 0
      )
        callingPolicy = serviceNumber.policyId.replace(
          new RegExp(",", "g"),
          ""
        );
      let serviceType = "";
      if (
        serviceNumber.serviceType !== null &&
        serviceNumber.serviceType.trim().length > 0
      )
        serviceType = serviceNumber.serviceType.replace(
          new RegExp(",", "g"),
          ""
        );

      let updateStatus = "";
      if (serviceNumber.updateStatus) updateStatus = serviceNumber.updateStatus;
      let errorMessage = "";
      if (serviceNumber.errorMessage)
        errorMessage = serviceNumber.errorMessage.replace(
          new RegExp(",", "g"),
          " "
        );

      return (
        displayName +
        "," +
        userName +
        "," +
        phoneNumber +
        "," +
        callingPolicy +
        "," +
        serviceType +
        "," +
        updateStatus +
        "," +
        errorMessage
      );
    });
  }

  return headerColumns.join("\n").concat(dataColumns.join("\n"));
}
export function getNumbers(extractType, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/users/numbers?extractType=${extractType}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getNumbers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getTenentResorces(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/tenant-resources",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getTenentResorces()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getTenentResorcesBulk(extractType, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/tenant-resources/bulk?extractType=${extractType}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getTenentResorcesBulk()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function updateTenentResorcesBulk(serviceNumberData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .put(
          AppConfigProps.serverRoutePrefix + "/tenant-resources/bulk",
          serviceNumberData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">updateTenentResorcesBulk()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function geSubscriberAccountNumbers(extractType, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/subscriber-account/numbers?extractType=${extractType}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">geSubscriberAccountNumbers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  });
}
